import { FieldSchema } from '../types';

/**
 * Sentinel value the backend uses to mask stored secrets.
 * An untouched field loaded with this value should be submitted as-is
 * so the backend knows to preserve the existing secret.
 */
export const REDACTED = 'REDACTED' as const;

/** User-facing placeholder shown in secret fields that hold a stored value. */
export const REDACTED_DISPLAY = '••••••••';

/** True when the string value is the backend's redaction sentinel. */
export function isRedacted(value: string | undefined | null): boolean {
  return value === REDACTED;
}

/**
 * Determine the submission value for a single secret field.
 *
 * @param currentValue  - The string currently in the form input
 * @param isDirty       - Whether the user has touched (typed into) the field since load
 * @param loadedAsRedacted - Whether the field was loaded with the REDACTED sentinel
 *
 * Logic:
 *  - Untouched REDACTED field  → submit "REDACTED" (preserve on backend)
 *  - User cleared the field    → submit null (clear on backend)
 *  - User entered a new value  → submit the new value (replace on backend)
 *  - Non-secret field or new site → pass through as-is
 */
export function secretFieldValue(
  currentValue: string,
  isDirty: boolean,
  loadedAsRedacted: boolean,
): string | null {
  if (!isDirty && loadedAsRedacted) {
    return REDACTED;
  }
  const trimmed = currentValue.trim();
  if (trimmed === '') {
    return null;
  }
  return trimmed;
}

/**
 * Build a provider config record for submission, applying secret-field
 * semantics to any field marked `secret: true` in the schema.
 *
 * @param fields       - The provider's field schema (from metadata)
 * @param values       - Current form values (all strings)
 * @param dirtyFields  - Set of field names the user has manually edited
 * @param loadedValues - Values originally loaded from the API (to detect REDACTED)
 */
export function prepareConfigForSubmit(
  fields: FieldSchema[],
  values: Record<string, string>,
  dirtyFields: ReadonlySet<string>,
  loadedValues: Record<string, string>,
): Record<string, unknown> {
  const config: Record<string, unknown> = {};

  for (const field of fields) {
    const raw = values[field.name];

    if (field.secret) {
      const resolved = secretFieldValue(
        raw ?? '',
        dirtyFields.has(field.name),
        isRedacted(loadedValues[field.name]),
      );
      // resolved is the new value, REDACTED (preserve stored), or null (clear);
      // send it through as-is either way.
      config[field.name] = resolved;
      continue;
    }

    // Non-secret fields: standard coercion
    if (raw !== undefined && raw !== '') {
      if (field.type === 'number') config[field.name] = parseFloat(raw);
      else if (field.type === 'integer') config[field.name] = parseInt(raw, 10);
      else if (field.type === 'boolean') config[field.name] = raw === 'true';
      else config[field.name] = raw;
    } else if (field.default != null) {
      config[field.name] = field.default;
    }
  }

  return config;
}
